import React from "react";
import { formatRelativeTime, formatAbsolute, parseBackendDate } from "../utils/date";
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  Card,
  CardContent,
  Chip,
  IconButton,
  Drawer,
  Divider,
  Tooltip,
  LinearProgress,
  Switch,
  FormControlLabel,
  InputAdornment,
} from "@mui/material";
import { alpha, useTheme, type SxProps, type Theme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { AppDialog, ConfirmDialog } from "../components/dialog";
import AddCircleOutlineRounded from "@mui/icons-material/AddCircleOutlineRounded";
import ArrowBackRounded from "@mui/icons-material/ArrowBackRounded";
import EditRounded from "@mui/icons-material/EditRounded";
import ArchiveRounded from "@mui/icons-material/ArchiveRounded";
import UnarchiveRounded from "@mui/icons-material/UnarchiveRounded";
import CloseRounded from "@mui/icons-material/CloseRounded";
import SearchIcon from "@mui/icons-material/Search";
import ClearRounded from "@mui/icons-material/ClearRounded";
import AutoAwesomeRounded from "@mui/icons-material/AutoAwesomeRounded";
import HistoryRounded from "@mui/icons-material/HistoryRounded";
import FunctionsRounded from "@mui/icons-material/FunctionsRounded";
import GavelRounded from "@mui/icons-material/GavelRounded";
import LabelRounded from "@mui/icons-material/LabelRounded";
import ChatBubbleOutlineRounded from "@mui/icons-material/ChatBubbleOutlineRounded";
import EditNoteRounded from "@mui/icons-material/EditNoteRounded";
import BuildRounded from "@mui/icons-material/BuildRounded";
import HelpOutlineRounded from "@mui/icons-material/HelpOutlineRounded";
import SaveRounded from "@mui/icons-material/SaveRounded";
import LightbulbOutlinedRounded from "@mui/icons-material/LightbulbOutlined";
import VerifiedOutlined from "@mui/icons-material/VerifiedOutlined";
import { buildApiUrl, tenantApiPrefix } from "../config";
import { getTenantIdFromStorage } from "../utils/onboardingLocalStorage";
import { SnackbarContext } from "../ui/SnackbarProvider";

// ============================================================================
// Types
// ============================================================================

interface KnowledgeNode {
  node_id: string;
  node_type: string;
  name: string;
  content: Record<string, any>;
  source_type: string;
  confidence: number;
  validation_count: number;
  scope: string;
  scope_user_id: string | null;
  last_validated: string | null;
  last_used: string | null;
  created_at: string;
  updated_at: string;
  archived_at: string | null;
}

interface KnowledgeStats {
  total: number;
  by_type: Record<string, number>;
  by_source: Record<string, number>;
  recent_count: number;
}

interface ChangelogEntry {
  changelog_id: string;
  action: string;
  actor_type: string;
  actor_id: string | null;
  previous_value: Record<string, any> | null;
  new_value: Record<string, any> | null;
  created_at: string;
}

type SortMode = "last_used" | "updated" | "confidence" | "validations" | "name";

// ============================================================================
// Type / source meta — single source of truth, drives chips, dropdowns, icons
// ============================================================================

type ChipColor = "primary" | "warning" | "default";
type Tone = "info" | "warning" | "neutral" | "success";

interface TypeMeta {
  label: string;
  chipColor: ChipColor;
  tone: Tone;
  iconNode: React.ReactElement;
  // Glyph used for the drawer hero (square coloured tile + icon).
  glyphIcon: React.ReactElement;
}

const TYPE_META: Record<string, TypeMeta> = {
  metric: {
    label: "Metric",
    chipColor: "primary",
    tone: "info",
    iconNode: <FunctionsRounded sx={{ fontSize: 14 }} />,
    glyphIcon: <FunctionsRounded sx={{ fontSize: 22 }} />,
  },
  business_rule: {
    label: "Rule",
    chipColor: "warning",
    tone: "warning",
    iconNode: <GavelRounded sx={{ fontSize: 14 }} />,
    glyphIcon: <GavelRounded sx={{ fontSize: 22 }} />,
  },
  term: {
    label: "Term",
    chipColor: "default",
    tone: "neutral",
    iconNode: <LabelRounded sx={{ fontSize: 14 }} />,
    glyphIcon: <LabelRounded sx={{ fontSize: 22 }} />,
  },
};

const TYPE_FILTER_ORDER = ["metric", "business_rule", "term"];

interface SourceMeta {
  label: string;
  // Component reference (not a constructed element) so callers can pass `sx`
  // and TypeScript can type-check it. Keeps icons sizable per usage instead
  // of locked to a single size when stored as a JSX element.
  Icon: React.ComponentType<{ sx?: SxProps<Theme> }>;
}

// Order matters — used for the Source filter dropdown.
const SOURCE_META: Record<string, SourceMeta> = {
  chat_definition: { label: "Chat", Icon: ChatBubbleOutlineRounded },
  manual: { label: "Manual", Icon: EditNoteRounded },
  sql_correction: { label: "SQL correction", Icon: BuildRounded },
  inferred: { label: "Inferred", Icon: AutoAwesomeRounded },
  clarification: { label: "Clarification", Icon: HelpOutlineRounded },
};

// ============================================================================
// Helpers
// ============================================================================

function useAuthHeaders() {
  return React.useMemo(() => {
    const token = window.localStorage.getItem("klaris_jwt");
    const headers: Record<string, string> = {};
    if (token) headers["Authorization"] = `Bearer ${token}`;
    return headers;
  }, []);
}

/** Debounce a value by `ms` milliseconds. */
function useDebouncedValue<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = React.useState(value);
  React.useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

function relativeTime(iso: string | null): string {
  if (!iso) return "—";
  return formatRelativeTime(iso);
}

function truncate(s: string, n = 100): string {
  const t = String(s || "");
  return t.length > n ? t.slice(0, n - 1) + "…" : t;
}

/** Confidence tone — green high, amber mid, red low. */
function confTone(c: number | null): "success" | "warning" | "error" | "muted" {
  if (c === null || typeof c !== "number") return "muted";
  if (c >= 0.8) return "success";
  if (c >= 0.5) return "warning";
  return "error";
}

/** Friendly label for a changelog actor. */
function formatActor(actor_type: string | null | undefined): string {
  switch ((actor_type || "").toLowerCase()) {
    case "user":
      return "You";
    case "agent":
    case "ai":
    case "system":
      return "AI";
    default:
      return actor_type || "—";
  }
}

/** Day bucket label for grouping history. */
function dayBucket(input: string): string {
  const d = parseBackendDate(input);
  if (!d) return "Unknown";
  const today = new Date();
  const yest = new Date();
  yest.setDate(today.getDate() - 1);
  const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  if (sameDay(d, today)) return "Today";
  if (sameDay(d, yest)) return "Yesterday";
  return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", year: d.getFullYear() === today.getFullYear() ? undefined : "numeric" }).format(d);
}

/**
 * Walk previous_value vs new_value and produce a list of human-readable
 * change descriptors. We surface the fields the UI exposes (name,
 * description, sql, node_type) — anything else is folded into a generic
 * "other fields".
 */
function summarizeChange(entry: ChangelogEntry): string[] {
  const prev = entry.previous_value || {};
  const next = entry.new_value || {};
  const out: string[] = [];

  const renameFromAction = (entry.action || "").toLowerCase();
  if (renameFromAction === "create") {
    out.push("Created");
    return out;
  }
  if (renameFromAction === "archive") {
    out.push("Archived");
    return out;
  }
  if (renameFromAction === "unarchive" || renameFromAction === "restore") {
    out.push("Restored");
    return out;
  }

  if (prev.name !== undefined && next.name !== undefined && prev.name !== next.name) {
    out.push(`Renamed: "${prev.name}" → "${next.name}"`);
  }
  const prevDef = prev.description ?? prev.content?.description;
  const nextDef = next.description ?? next.content?.description;
  if (prevDef !== undefined && nextDef !== undefined && prevDef !== nextDef) {
    out.push("Definition updated");
  }
  const prevSql = prev.sql ?? prev.content?.sql ?? prev.content?.formula_sql ?? prev.content?.rule_sql;
  const nextSql = next.sql ?? next.content?.sql ?? next.content?.formula_sql ?? next.content?.rule_sql;
  if (prevSql !== undefined && nextSql !== undefined && prevSql !== nextSql) {
    out.push("SQL updated");
  }
  if (prev.node_type !== undefined && next.node_type !== undefined && prev.node_type !== next.node_type) {
    out.push(`Type: ${prev.node_type} → ${next.node_type}`);
  }

  if (out.length === 0) {
    out.push((entry.action || "Updated").replace(/_/g, " "));
  }
  return out;
}

// ============================================================================
// Main page
// ============================================================================

export default function KnowledgeBasePage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { notify } = React.useContext(SnackbarContext);
  const headers = useAuthHeaders();

  // Data
  const [nodes, setNodes] = React.useState<KnowledgeNode[]>([]);
  const [stats, setStats] = React.useState<KnowledgeStats | null>(null);
  const [loading, setLoading] = React.useState(true);

  // Filters
  const [typeFilter, setTypeFilter] = React.useState<string>("");
  const [sourceFilter, setSourceFilter] = React.useState<string>("");
  const [searchQuery, setSearchQuery] = React.useState<string>("");
  const debouncedSearch = useDebouncedValue(searchQuery, 250);
  const [showArchived, setShowArchived] = React.useState(false);
  const [sortMode, setSortMode] = React.useState<SortMode>("last_used");

  // Pagination
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  // Detail panel
  const [selectedNode, setSelectedNode] = React.useState<KnowledgeNode | null>(null);
  const [detailOpen, setDetailOpen] = React.useState(false);
  const [changelog, setChangelog] = React.useState<ChangelogEntry[]>([]);
  const [changelogLoading, setChangelogLoading] = React.useState(false);

  // Edit state
  const [editing, setEditing] = React.useState(false);
  const [editName, setEditName] = React.useState("");
  const [editDefinition, setEditDefinition] = React.useState("");
  const [editFormula, setEditFormula] = React.useState("");
  const [editType, setEditType] = React.useState("");
  const [saving, setSaving] = React.useState(false);

  // Archive confirm
  const [archiveConfirmOpen, setArchiveConfirmOpen] = React.useState(false);
  const [archiveTarget, setArchiveTarget] = React.useState<KnowledgeNode | null>(null);
  const [archiveBusy, setArchiveBusy] = React.useState(false);

  // Add modal
  const [addOpen, setAddOpen] = React.useState(false);
  const [addName, setAddName] = React.useState("");
  const [addType, setAddType] = React.useState("metric");
  const [addDefinition, setAddDefinition] = React.useState("");
  const [addFormula, setAddFormula] = React.useState("");
  const [addScope, setAddScope] = React.useState("tenant");
  const [addLoading, setAddLoading] = React.useState(false);

  // ── Fetch data ───────────────────────────────────────────────────

  const fetchNodes = React.useCallback(async () => {
    setLoading(true);
    try {
      const tid = getTenantIdFromStorage();
      if (!tid) return;
      const params = new URLSearchParams();
      if (typeFilter) params.set("node_type", typeFilter);
      if (sourceFilter) params.set("source_type", sourceFilter);
      if (debouncedSearch.trim()) params.set("q", debouncedSearch.trim());
      if (showArchived) params.set("include_archived", "true");

      const resp = await fetch(
        buildApiUrl(`${tenantApiPrefix(tid)}/knowledge/nodes?${params.toString()}`),
        { headers } as RequestInit
      );
      if (!resp.ok) throw new Error("Failed to fetch");
      const data = await resp.json();
      setNodes(data.nodes || []);
    } catch (e) {
      console.error("Failed to fetch knowledge nodes:", e);
    } finally {
      setLoading(false);
    }
  }, [headers, typeFilter, sourceFilter, debouncedSearch, showArchived]);

  const fetchStats = React.useCallback(async () => {
    try {
      const tid = getTenantIdFromStorage();
      if (!tid) return;
      const resp = await fetch(buildApiUrl(`${tenantApiPrefix(tid)}/knowledge/stats`), { headers } as RequestInit);
      if (!resp.ok) return;
      setStats(await resp.json());
    } catch (e) {
      console.error("Failed to fetch stats:", e);
    }
  }, [headers]);

  React.useEffect(() => {
    fetchNodes();
    fetchStats();
  }, [fetchNodes, fetchStats]);

  React.useEffect(() => {
    setPage(0);
  }, [typeFilter, sourceFilter, debouncedSearch, showArchived, sortMode]);

  // ── Sorting ──────────────────────────────────────────────────────

  const sortedNodes = React.useMemo(() => {
    const list = [...nodes];
    const ts = (v: string | null | undefined): number => {
      if (!v) return 0;
      const d = parseBackendDate(v);
      return d ? d.getTime() : 0;
    };
    // "Recent activity": last_used if set, otherwise updated_at. This stops
    // brand-new definitions (last_used = null) from being dumped at the
    // bottom of the default sort.
    const activity = (n: KnowledgeNode): number => ts(n.last_used) || ts(n.updated_at);

    switch (sortMode) {
      case "last_used":
        return list.sort((a, b) => activity(b) - activity(a));
      case "updated":
        return list.sort((a, b) => ts(b.updated_at) - ts(a.updated_at));
      case "confidence":
        // Tiebreak by recency so freshly-added items don't get buried inside
        // ties (e.g. a flock of 60%-confidence definitions).
        return list.sort((a, b) => {
          const d = (b.confidence || 0) - (a.confidence || 0);
          return d !== 0 ? d : activity(b) - activity(a);
        });
      case "validations":
        return list.sort((a, b) => {
          const d = (b.validation_count || 0) - (a.validation_count || 0);
          return d !== 0 ? d : activity(b) - activity(a);
        });
      case "name":
        return list.sort((a, b) => a.name.localeCompare(b.name));
      default:
        return list;
    }
  }, [nodes, sortMode]);

  const paginatedNodes = React.useMemo(() => {
    const start = page * rowsPerPage;
    return sortedNodes.slice(start, start + rowsPerPage);
  }, [sortedNodes, page, rowsPerPage]);

  // ── Detail panel ─────────────────────────────────────────────────

  const openDetail = React.useCallback(
    async (node: KnowledgeNode) => {
      setSelectedNode(node);
      setDetailOpen(true);
      setEditing(false);
      setChangelogLoading(true);
      try {
        const tid = getTenantIdFromStorage();
        if (!tid) return;
        const resp = await fetch(
          buildApiUrl(`${tenantApiPrefix(tid)}/knowledge/nodes/${node.node_id}/history`),
          { headers } as RequestInit
        );
        if (resp.ok) {
          setChangelog(await resp.json());
        }
      } catch {
        setChangelog([]);
      } finally {
        setChangelogLoading(false);
      }
    },
    [headers]
  );

  const closeDetail = React.useCallback(() => {
    if (saving) return;
    setDetailOpen(false);
    setEditing(false);
  }, [saving]);

  const startEdit = React.useCallback(() => {
    if (!selectedNode) return;
    setEditName(selectedNode.name);
    setEditDefinition(selectedNode.content?.description || "");
    setEditFormula(
      selectedNode.content?.sql ||
        selectedNode.content?.formula_sql ||
        selectedNode.content?.rule_sql ||
        ""
    );
    setEditType(selectedNode.node_type);
    setEditing(true);
  }, [selectedNode]);

  const saveEdit = React.useCallback(async () => {
    if (!selectedNode) return;
    setSaving(true);
    try {
      const tid = getTenantIdFromStorage();
      if (!tid) return;
      const body: any = {};
      if (editName !== selectedNode.name) body.name = editName;
      if (editDefinition !== (selectedNode.content?.description || "")) body.definition = editDefinition;
      const currentSql =
        selectedNode.content?.sql ||
        selectedNode.content?.formula_sql ||
        selectedNode.content?.rule_sql ||
        "";
      if (editFormula !== currentSql) body.sql = editFormula;
      if (editType !== selectedNode.node_type) body.node_type = editType;

      const resp = await fetch(
        buildApiUrl(`${tenantApiPrefix(tid)}/knowledge/nodes/${selectedNode.node_id}`),
        {
          method: "PUT",
          headers: { ...headers, "Content-Type": "application/json" },
          body: JSON.stringify(body),
        } as RequestInit
      );
      if (!resp.ok) throw new Error("Update failed");
      const updated = await resp.json();
      setSelectedNode(updated);
      setEditing(false);
      notify("Definition updated", "success");
      fetchNodes();
      fetchStats();
    } catch (e) {
      notify("Failed to save changes", "error");
    } finally {
      setSaving(false);
    }
  }, [
    selectedNode,
    editName,
    editDefinition,
    editFormula,
    editType,
    headers,
    notify,
    fetchNodes,
    fetchStats,
  ]);

  // Run the actual archive / restore call. Restore is one-click; archive
  // routes through a ConfirmDialog so users can't lose definitions by
  // misclick.
  const performArchive = React.useCallback(
    async (node: KnowledgeNode) => {
      const action = node.archived_at ? "unarchive" : "archive";
      try {
        setArchiveBusy(true);
        const tid = getTenantIdFromStorage();
        if (!tid) return;
        const resp = await fetch(
          buildApiUrl(`${tenantApiPrefix(tid)}/knowledge/nodes/${node.node_id}/${action}`),
          { method: "POST", headers } as RequestInit
        );
        if (!resp.ok) throw new Error();
        notify(action === "archive" ? "Definition archived" : "Definition restored", "success");
        setDetailOpen(false);
        setSelectedNode(null);
        setArchiveConfirmOpen(false);
        setArchiveTarget(null);
        fetchNodes();
        fetchStats();
      } catch {
        notify(`Failed to ${action}`, "error");
      } finally {
        setArchiveBusy(false);
      }
    },
    [headers, notify, fetchNodes, fetchStats]
  );

  const requestArchive = React.useCallback((node: KnowledgeNode) => {
    if (node.archived_at) {
      // Restore — no confirm needed.
      performArchive(node);
    } else {
      setArchiveTarget(node);
      setArchiveConfirmOpen(true);
    }
  }, [performArchive]);

  // ── Add definition ───────────────────────────────────────────────

  const handleAdd = React.useCallback(async () => {
    if (!addName.trim() || !addDefinition.trim()) {
      notify("Name and definition are required", "error");
      return;
    }
    setAddLoading(true);
    try {
      const tid = getTenantIdFromStorage();
      if (!tid) {
        notify("Not authenticated", "error");
        return;
      }
      const body: any = {
        name: addName.trim(),
        node_type: addType,
        definition: addDefinition.trim(),
        scope: addScope,
      };
      if (addFormula.trim()) body.sql = addFormula.trim();

      const resp = await fetch(buildApiUrl(`${tenantApiPrefix(tid)}/knowledge/nodes`), {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      } as RequestInit);
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || "Failed to create");
      }
      notify("Definition created", "success");
      setAddOpen(false);
      setAddName("");
      setAddDefinition("");
      setAddFormula("");
      setAddType("metric");
      setAddScope("tenant");
      fetchNodes();
      fetchStats();
    } catch (e: any) {
      notify(e.message || "Failed to create definition", "error");
    } finally {
      setAddLoading(false);
    }
  }, [addName, addType, addDefinition, addFormula, addScope, headers, notify, fetchNodes, fetchStats]);

  // ── Render ───────────────────────────────────────────────────────

  return (
    <Box>
      {/* Page Header — slim toolbar (page title is rendered by DashboardLayout) */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          justifyContent: "space-between",
          gap: { xs: 1.5, sm: 2 },
          mb: 2,
          pb: 1.5,
          borderBottom: (t: any) => `1px solid ${t.tokens.border.subtle}`,
          minHeight: { sm: 36 },
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8125rem", lineHeight: 1.45 }}>
          Metrics, business rules, and terms learned from conversations
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddCircleOutlineRounded />}
          onClick={() => setAddOpen(true)}
          size="small"
          sx={{
            borderRadius: "7px",
            textTransform: "none",
            height: 32,
            fontSize: "0.8125rem",
            fontWeight: 600,
            boxShadow: "none",
            alignSelf: { xs: "stretch", sm: "auto" },
            "&:hover": { boxShadow: "none" },
          }}
        >
          {isMobile ? "Add" : "Add Definition"}
        </Button>
      </Box>

      {/* Stats tiles */}
      {stats && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(4, minmax(0, 1fr))" },
            gap: 1.5,
            mb: 2.5,
          }}
        >
          <StatTile
            label="Metrics"
            value={stats.by_type.metric || 0}
            icon={<FunctionsRounded sx={{ fontSize: 16 }} />}
            tone="info"
          />
          <StatTile
            label="Rules"
            value={stats.by_type.business_rule || 0}
            icon={<GavelRounded sx={{ fontSize: 16 }} />}
            tone="warning"
          />
          <StatTile
            label="Terms"
            value={stats.by_type.term || 0}
            icon={<LabelRounded sx={{ fontSize: 16 }} />}
            tone="neutral"
          />
          <StatTile
            label="Learned this week"
            value={stats.recent_count}
            icon={<AutoAwesomeRounded sx={{ fontSize: 16 }} />}
            tone="success"
          />
        </Box>
      )}

      {/* Filters */}
      <Card sx={{ mb: 2 }}>
        <CardContent
          sx={{
            py: { xs: 1.25, sm: 1.5 },
            px: { xs: 1.5, sm: 2 },
            "&:last-child": { pb: { xs: 1.25, sm: 1.5 } },
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.25}
            alignItems={{ xs: "stretch", sm: "center" }}
            useFlexGap
            flexWrap="wrap"
          >
            <FormControl size="small" sx={{ minWidth: { xs: 0, sm: 130 }, width: { xs: "100%", sm: "auto" } }}>
              <InputLabel>Type</InputLabel>
              <Select value={typeFilter} label="Type" onChange={(e) => setTypeFilter(e.target.value)}>
                <MenuItem value="">All Types</MenuItem>
                {TYPE_FILTER_ORDER.map((k) => (
                  <MenuItem key={k} value={k}>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      {TYPE_META[k].iconNode}
                      <span>{TYPE_META[k].label}</span>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: { xs: 0, sm: 170 }, width: { xs: "100%", sm: "auto" } }}>
              <InputLabel>Source</InputLabel>
              <Select value={sourceFilter} label="Source" onChange={(e) => setSourceFilter(e.target.value)}>
                <MenuItem value="">All Sources</MenuItem>
                {Object.entries(SOURCE_META).map(([k, m]) => (
                  <MenuItem key={k} value={k}>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <m.Icon sx={{ fontSize: 14 }} />
                      <span>{m.label}</span>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: { xs: 0, sm: 180 }, width: { xs: "100%", sm: "auto" } }}>
              <InputLabel>Sort by</InputLabel>
              <Select
                value={sortMode}
                label="Sort by"
                onChange={(e) => setSortMode(e.target.value as SortMode)}
              >
                <MenuItem value="last_used">Recently used</MenuItem>
                <MenuItem value="updated">Recently updated</MenuItem>
                <MenuItem value="confidence">Highest confidence</MenuItem>
                <MenuItem value="validations">Most validated</MenuItem>
                <MenuItem value="name">Name (A → Z)</MenuItem>
              </Select>
            </FormControl>

            <TextField
              size="small"
              placeholder="Search definitions…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ mr: 0 }}>
                    <SearchIcon sx={{ color: "text.disabled", fontSize: 20 }} />
                  </InputAdornment>
                ),
                endAdornment: searchQuery ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSearchQuery("")}
                      aria-label="Clear search"
                      sx={{ p: 0.25 }}
                    >
                      <ClearRounded sx={{ fontSize: 16 }} />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
              }}
              sx={{ flex: 1, minWidth: { xs: "100%", sm: 220 }, maxWidth: { xs: "none", sm: 360 } }}
            />

            <FormControlLabel
              sx={{
                ml: { xs: 0, sm: "auto" },
                mr: 0,
                alignSelf: { xs: "flex-start", sm: "auto" },
                "& .MuiFormControlLabel-label": {
                  fontSize: "0.8125rem",
                  color: "text.secondary",
                },
              }}
              control={
                <Switch
                  size="small"
                  checked={showArchived}
                  onChange={(e) => setShowArchived(e.target.checked)}
                />
              }
              label="Include archived"
            />
          </Stack>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
          {loading ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <CircularProgress size={32} />
            </Box>
          ) : nodes.length === 0 ? (
            <EmptyState
              filtered={Boolean(searchQuery || typeFilter || sourceFilter)}
              onClearFilters={() => {
                setSearchQuery("");
                setTypeFilter("");
                setSourceFilter("");
              }}
              onAdd={() => setAddOpen(true)}
            />
          ) : (
            <>
              {isMobile ? (
                <Stack spacing={1.25} sx={{ px: { xs: 1.5, sm: 0 }, py: { xs: 1.5, sm: 0 } }}>
                  {paginatedNodes.map((node) => {
                    const typeMeta = TYPE_META[node.node_type] || TYPE_META.term;
                    const sourceMeta = SOURCE_META[node.source_type];
                    const isArchived = !!node.archived_at;
                    return (
                      <Paper
                        key={node.node_id}
                        variant="outlined"
                        onClick={() => openDetail(node)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            openDetail(node);
                          }
                        }}
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          cursor: "pointer",
                          opacity: isArchived ? 0.55 : 1,
                          transition: "background-color 0.15s ease",
                          "&:hover": { bgcolor: "action.hover" },
                          "&:focus-visible": { outline: (t) => `2px solid ${t.tokens.brand.primary}`, outlineOffset: 2 },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            gap: 1,
                          }}
                        >
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography
                              variant="subtitle2"
                              fontWeight={700}
                              sx={{ wordBreak: "break-word", lineHeight: 1.3 }}
                            >
                              {node.name}
                            </Typography>
                            {isArchived && (
                              <Chip
                                label="archived"
                                size="small"
                                sx={{ mt: 0.5, height: 18, fontSize: "0.6875rem" }}
                              />
                            )}
                          </Box>
                          <Tooltip title={isArchived ? "Restore" : "Archive"}>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                requestArchive(node);
                              }}
                              aria-label={isArchived ? "Restore" : "Archive"}
                            >
                              {isArchived ? (
                                <UnarchiveRounded fontSize="small" />
                              ) : (
                                <ArchiveRounded fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                        </Box>
                        <Stack
                          direction="row"
                          spacing={0.75}
                          alignItems="center"
                          flexWrap="wrap"
                          sx={{ mt: 1, gap: 0.75 }}
                        >
                          <Chip
                            icon={typeMeta.iconNode}
                            label={typeMeta.label}
                            color={typeMeta.chipColor}
                            size="small"
                            variant="outlined"
                            sx={{ height: 24, fontSize: "0.75rem" }}
                          />
                          {sourceMeta && (
                            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: "text.secondary" }}>
                              <sourceMeta.Icon sx={{ fontSize: 14 }} />
                              <Typography variant="caption" sx={{ fontSize: "0.75rem" }}>
                                {sourceMeta.label}
                              </Typography>
                            </Stack>
                          )}
                        </Stack>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 1, fontSize: "0.8125rem", lineHeight: 1.45 }}
                        >
                          {truncate(node.content?.description || "", 140)}
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mt: 1.25,
                          }}
                        >
                          <ConfidenceDots value={node.confidence} />
                          <Tooltip title={node.last_used ? formatAbsolute(node.last_used) : ""}>
                            <Typography variant="caption" color="text.secondary">
                              {relativeTime(node.last_used)}
                            </Typography>
                          </Tooltip>
                        </Box>
                      </Paper>
                    );
                  })}
                </Stack>
              ) : (
                <Box sx={{ width: "100%", overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
                  <Table
                    size="small"
                    sx={{
                      minWidth: 900,
                      // Show row action only on row hover/focus.
                      "& .MuiTableRow-root .row-action": {
                        opacity: 0,
                        transition: "opacity 120ms ease",
                      },
                      "& .MuiTableRow-root:hover .row-action, & .MuiTableRow-root:focus-within .row-action": {
                        opacity: 1,
                      },
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, width: 220 }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 600, width: 90 }}>Type</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Definition</TableCell>
                        <TableCell sx={{ fontWeight: 600, width: 130 }}>Source</TableCell>
                        <TableCell sx={{ fontWeight: 600, width: 110 }}>Confidence</TableCell>
                        <TableCell sx={{ fontWeight: 600, width: 110 }}>Last used</TableCell>
                        <TableCell sx={{ fontWeight: 600, width: 56 }} align="right" />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedNodes.map((node) => {
                        const typeMeta = TYPE_META[node.node_type] || TYPE_META.term;
                        const sourceMeta = SOURCE_META[node.source_type];
                        const isArchived = !!node.archived_at;
                        return (
                          <TableRow
                            key={node.node_id}
                            hover
                            role="button"
                            tabIndex={0}
                            onClick={() => openDetail(node)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                openDetail(node);
                              }
                            }}
                            sx={{
                              cursor: "pointer",
                              opacity: isArchived ? 0.5 : 1,
                              "&:hover": { bgcolor: "action.hover" },
                              "&:focus-visible": {
                                outline: (t) => `2px solid ${t.tokens.brand.primary}`,
                                outlineOffset: -2,
                              },
                            }}
                          >
                            <TableCell sx={{ fontWeight: 500 }}>
                              {node.name}
                              {isArchived && (
                                <Chip
                                  label="archived"
                                  size="small"
                                  sx={{ ml: 1, height: 18, fontSize: "0.6875rem" }}
                                />
                              )}
                            </TableCell>
                            <TableCell>
                              <Chip
                                icon={typeMeta.iconNode}
                                label={typeMeta.label}
                                color={typeMeta.chipColor}
                                size="small"
                                variant="outlined"
                                sx={{ height: 24, fontSize: "0.75rem" }}
                              />
                            </TableCell>
                            <TableCell sx={{ color: "text.secondary" }}>
                              {truncate(node.content?.description || "", 80)}
                            </TableCell>
                            <TableCell>
                              {sourceMeta ? (
                                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: "text.secondary" }}>
                                  <sourceMeta.Icon sx={{ fontSize: 14 }} />
                                  <Typography variant="caption" sx={{ fontSize: "0.75rem" }}>
                                    {sourceMeta.label}
                                  </Typography>
                                </Stack>
                              ) : (
                                <Typography variant="caption" sx={{ fontSize: "0.75rem" }}>
                                  {node.source_type}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <ConfidenceDots value={node.confidence} />
                            </TableCell>
                            <TableCell>
                              <Tooltip title={node.last_used ? formatAbsolute(node.last_used) : ""}>
                                <Typography variant="caption" color="text.secondary">
                                  {relativeTime(node.last_used)}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                            <TableCell
                              align="right"
                              onClick={(e) => e.stopPropagation()}
                              sx={{ pr: 1 }}
                            >
                              <Tooltip title={isArchived ? "Restore" : "Archive"}>
                                <IconButton
                                  size="small"
                                  className="row-action"
                                  onClick={() => requestArchive(node)}
                                  aria-label={isArchived ? "Restore" : "Archive"}
                                >
                                  {isArchived ? (
                                    <UnarchiveRounded fontSize="small" />
                                  ) : (
                                    <ArchiveRounded fontSize="small" />
                                  )}
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Box>
              )}
              <TablePagination
                component="div"
                count={sortedNodes.length}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50]}
                sx={{
                  borderTop: 1,
                  borderColor: "divider",
                  px: { xs: 0.5, sm: 0 },
                  "& .MuiTablePagination-toolbar": { flexWrap: "wrap", gap: 1 },
                }}
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* Detail drawer */}
      <Drawer
        anchor="right"
        open={detailOpen}
        onClose={(_, reason) => {
          if (saving && (reason === "backdropClick" || reason === "escapeKeyDown")) return;
          closeDetail();
        }}
        PaperProps={{ sx: { width: { xs: "100%", sm: 560, md: 600 }, p: { xs: 2, sm: 3 } } }}
      >
        {selectedNode && (
          <DetailPanel
            node={selectedNode}
            changelog={changelog}
            changelogLoading={changelogLoading}
            editing={editing}
            editName={editName}
            editDefinition={editDefinition}
            editFormula={editFormula}
            editType={editType}
            saving={saving}
            onClose={closeDetail}
            onStartEdit={startEdit}
            onCancelEdit={() => setEditing(false)}
            onSave={saveEdit}
            onArchive={() => requestArchive(selectedNode)}
            setEditName={setEditName}
            setEditDefinition={setEditDefinition}
            setEditFormula={setEditFormula}
            setEditType={setEditType}
          />
        )}
      </Drawer>

      {/* Archive confirm */}
      <ConfirmDialog
        open={archiveConfirmOpen}
        onClose={() => {
          if (!archiveBusy) {
            setArchiveConfirmOpen(false);
            setArchiveTarget(null);
          }
        }}
        onConfirm={() => {
          if (archiveTarget) performArchive(archiveTarget);
        }}
        tone="warning"
        title="Archive definition"
        description={
          archiveTarget
            ? `"${archiveTarget.name}" will be moved to archive. The AI will stop using it, but it will be kept for reference and you can restore it any time.`
            : ""
        }
        confirmLabel="Archive"
        busy={archiveBusy}
      />

      {/* Add definition modal */}
      <AppDialog
        open={addOpen}
        onClose={() => { setAddOpen(false); setAddScope("tenant"); }}
        size="sm"
        title="Add definition"
        busy={addLoading}
        primaryAction={{
          label: "Save",
          onClick: handleAdd,
          disabled: !addName.trim() || !addDefinition.trim(),
        }}
        secondaryAction={{ label: "Cancel", color: "inherit", onClick: () => { setAddOpen(false); setAddScope("tenant"); } }}
      >
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Name"
            size="small"
            value={addName}
            onChange={(e) => setAddName(e.target.value)}
            placeholder="e.g. Net Revenue"
            fullWidth
          />
          <FormControl size="small" fullWidth>
            <InputLabel>Type</InputLabel>
            <Select value={addType} label="Type" onChange={(e) => setAddType(e.target.value)}>
              <MenuItem value="metric">Metric</MenuItem>
              <MenuItem value="business_rule">Business Rule</MenuItem>
              <MenuItem value="term">Term</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel>Visibility</InputLabel>
            <Select value={addScope} label="Visibility" onChange={(e) => setAddScope(e.target.value)}>
              <MenuItem value="tenant">Org-wide</MenuItem>
              <MenuItem value="user">Personal</MenuItem>
            </Select>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, fontSize: "0.75rem", lineHeight: 1.4 }}>
              {addScope === "tenant"
                ? "Visible to everyone in your workspace. The AI will use this for all users."
                : "Only visible to you. The AI will apply this definition only in your conversations."}
            </Typography>
          </FormControl>
          <TextField
            label="Definition"
            size="small"
            value={addDefinition}
            onChange={(e) => setAddDefinition(e.target.value)}
            placeholder="How is this calculated or defined?"
            multiline
            rows={3}
            fullWidth
          />
          <TextField
            label={
              addType === "business_rule"
                ? "SQL Condition (optional)"
                : addType === "metric"
                ? "SQL Formula (optional)"
                : "SQL (optional)"
            }
            size="small"
            value={addFormula}
            onChange={(e) => setAddFormula(e.target.value)}
            placeholder={
              addType === "business_rule"
                ? "status = 'confirmed' AND created_at > NOW() - INTERVAL '90 days'"
                : "SUM(amount) - SUM(refunds)"
            }
            fullWidth
            sx={{ fontFamily: "monospace" }}
          />
        </Stack>
      </AppDialog>
    </Box>
  );
}

// ============================================================================
// Stat tile
// ============================================================================

function StatTile({
  label,
  value,
  icon,
  tone,
}: {
  label: string;
  value: number;
  icon: React.ReactElement;
  tone: Tone;
}) {
  return (
    <Paper
      variant="outlined"
      sx={(t) => {
        const tonePalette: Record<Tone, string> = {
          info: t.tokens.semantic.info,
          warning: t.tokens.semantic.warning,
          success: t.tokens.semantic.success,
          neutral: t.tokens.text.secondary,
        };
        const c = tonePalette[tone];
        return {
          p: { xs: 1.25, sm: 1.5 },
          borderRadius: `${t.ds.radius.md}px`,
          borderColor: t.tokens.border.subtle,
          bgcolor: t.tokens.surface.raised,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
          ".kb-stat-icon": {
            width: 28,
            height: 28,
            borderRadius: `${t.ds.radius.sm}px`,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            color: c,
            backgroundColor: alpha(c, 0.12),
          },
          ".kb-stat-value": { color: c },
        };
      }}
    >
      <Box className="kb-stat-icon">{icon}</Box>
      <Typography
        className="kb-stat-value"
        variant="h5"
        sx={{ fontWeight: 700, fontSize: { xs: "1.25rem", sm: "1.5rem" }, lineHeight: 1.1 }}
      >
        {value}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          fontSize: { xs: "0.6875rem", sm: "0.75rem" },
          lineHeight: 1.3,
          textTransform: "uppercase",
          letterSpacing: 0.6,
        }}
      >
        {label}
      </Typography>
    </Paper>
  );
}

// ============================================================================
// Empty state
// ============================================================================

function EmptyState({
  filtered,
  onClearFilters,
  onAdd,
}: {
  filtered: boolean;
  onClearFilters: () => void;
  onAdd: () => void;
}) {
  return (
    <Box sx={{ p: { xs: 4, sm: 6 }, textAlign: "center" }}>
      <Box
        sx={(t) => ({
          width: 56,
          height: 56,
          borderRadius: `${t.ds.radius.md}px`,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          color: t.tokens.brand.primary,
          backgroundColor: alpha(t.tokens.brand.primary, 0.1),
          mb: 1.5,
        })}
      >
        <LightbulbOutlinedRounded sx={{ fontSize: 28 }} />
      </Box>
      <Typography variant="h6" sx={{ fontSize: "1rem", mb: 0.5 }}>
        {filtered ? "No definitions match these filters" : "No definitions yet"}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, maxWidth: 360, mx: "auto" }}>
        {filtered
          ? "Try clearing filters or broadening your search."
          : "Definitions will appear here as the AI learns from your conversations — or you can add one manually."}
      </Typography>
      {filtered ? (
        <Button variant="outlined" size="small" onClick={onClearFilters}>
          Clear filters
        </Button>
      ) : (
        <Button
          variant="contained"
          size="small"
          startIcon={<AddCircleOutlineRounded />}
          onClick={onAdd}
          sx={{ textTransform: "none", boxShadow: "none", "&:hover": { boxShadow: "none" } }}
        >
          Add your first definition
        </Button>
      )}
    </Box>
  );
}

// ============================================================================
// Confidence visualisations
// ============================================================================

function ConfidenceDots({ value }: { value: number }) {
  const filled = Math.round(value * 4);
  const tone = confTone(value);
  const colorMap: Record<string, string> = {
    success: "success.main",
    warning: "warning.main",
    error: "error.main",
    muted: "action.disabled",
  };
  return (
    <Tooltip title={`${(value * 100).toFixed(0)}% confidence`}>
      <Box sx={{ display: "flex", gap: 0.4 }}>
        {[0, 1, 2, 3].map((i) => (
          <Box
            key={i}
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              bgcolor: i < filled ? colorMap[tone] : "action.disabledBackground",
            }}
          />
        ))}
      </Box>
    </Tooltip>
  );
}

function ConfidenceBar({ value }: { value: number | null }) {
  const tone = confTone(value);
  const theme = useTheme();
  const colorMap: Record<string, string> = {
    success: theme.palette.success.main,
    warning: theme.palette.warning.main,
    error: theme.palette.error.main,
    muted: theme.palette.divider,
  };
  const c = colorMap[tone];
  const pct = value === null || typeof value !== "number" ? 0 : Math.max(0, Math.min(100, value * 100));
  return (
    <Stack spacing={0.5} sx={{ minWidth: 80 }}>
      <Typography variant="caption" sx={{ fontSize: "0.75rem", fontWeight: 600, color: c }}>
        {value === null ? "—" : `${Math.round(pct)}%`}
      </Typography>
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{
          height: 4,
          borderRadius: 2,
          bgcolor: alpha(c, 0.15),
          "& .MuiLinearProgress-bar": { backgroundColor: c },
        }}
      />
    </Stack>
  );
}

// ============================================================================
// Drawer detail panel
// ============================================================================

function DetailPanel({
  node,
  changelog,
  changelogLoading,
  editing,
  editName,
  editDefinition,
  editFormula,
  editType,
  saving,
  onClose,
  onStartEdit,
  onCancelEdit,
  onSave,
  onArchive,
  setEditName,
  setEditDefinition,
  setEditFormula,
  setEditType,
}: {
  node: KnowledgeNode;
  changelog: ChangelogEntry[];
  changelogLoading: boolean;
  editing: boolean;
  editName: string;
  editDefinition: string;
  editFormula: string;
  editType: string;
  saving: boolean;
  onClose: () => void;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onSave: () => void;
  onArchive: () => void;
  setEditName: (v: string) => void;
  setEditDefinition: (v: string) => void;
  setEditFormula: (v: string) => void;
  setEditType: (v: string) => void;
}) {
  const theme = useTheme();
  const isMobileDetail = useMediaQuery(theme.breakpoints.down("md"));
  const typeMeta = TYPE_META[node.node_type] || TYPE_META.term;
  const sourceMeta = SOURCE_META[node.source_type];
  const sqlValue =
    node.content?.sql ||
    node.content?.formula_sql ||
    node.content?.rule_sql ||
    "";
  const sqlLabel =
    node.node_type === "business_rule"
      ? "SQL Condition"
      : node.node_type === "metric"
      ? "SQL Formula"
      : "SQL";

  // cmd/ctrl-S to save while editing.
  React.useEffect(() => {
    if (!editing) return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        if (!saving) onSave();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [editing, saving, onSave]);

  // Group changelog entries by day for readable history.
  const groupedChangelog = React.useMemo(() => {
    const groups: { day: string; items: ChangelogEntry[] }[] = [];
    for (const entry of changelog) {
      const day = dayBucket(entry.created_at);
      const last = groups[groups.length - 1];
      if (last && last.day === day) {
        last.items.push(entry);
      } else {
        groups.push({ day, items: [entry] });
      }
    }
    return groups;
  }, [changelog]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {isMobileDetail && (
        <Button
          size="small"
          variant="text"
          startIcon={<ArrowBackRounded sx={{ fontSize: 20 }} />}
          onClick={onClose}
          sx={{
            mb: 1.5,
            alignSelf: "flex-start",
            textTransform: "none",
            fontWeight: 600,
            color: "text.secondary",
            px: 0.5,
            minWidth: 0,
            "&:hover": { bgcolor: "action.hover" },
          }}
          aria-label="Back to knowledge list"
        >
          Back to list
        </Button>
      )}

      {/* Hero: glyph + name + sub-line + action cluster */}
      <Stack direction="row" alignItems="flex-start" spacing={1.5} sx={{ mb: 2 }}>
        <TypeGlyph tone={typeMeta.tone} icon={typeMeta.glyphIcon} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {editing ? (
            <TextField
              size="small"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              autoFocus
              fullWidth
              sx={{ mb: 0.5 }}
            />
          ) : (
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.25, wordBreak: "break-word" }}>
              {node.name}
            </Typography>
          )}
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            useFlexGap
            flexWrap="wrap"
            sx={{ mt: 0.5, color: "text.secondary" }}
          >
            <Chip
              icon={typeMeta.iconNode}
              label={typeMeta.label}
              color={typeMeta.chipColor}
              size="small"
              variant="outlined"
              sx={{ height: 22, fontSize: "0.6875rem" }}
            />
            {sourceMeta && (
              <Stack direction="row" spacing={0.4} alignItems="center">
                <Box
                  sx={{
                    width: 3,
                    height: 3,
                    borderRadius: "50%",
                    bgcolor: "text.disabled",
                  }}
                />
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <sourceMeta.Icon sx={{ fontSize: 14 }} />
                  <Typography variant="caption" sx={{ fontSize: "0.75rem" }}>
                    {sourceMeta.label}
                  </Typography>
                </Stack>
              </Stack>
            )}
            {node.validation_count > 0 && (
              <Stack direction="row" spacing={0.4} alignItems="center">
                <Box
                  sx={{
                    width: 3,
                    height: 3,
                    borderRadius: "50%",
                    bgcolor: "text.disabled",
                  }}
                />
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <VerifiedOutlined sx={{ fontSize: 14 }} />
                  <Typography variant="caption" sx={{ fontSize: "0.75rem" }}>
                    {node.validation_count} validations
                  </Typography>
                </Stack>
              </Stack>
            )}
          </Stack>
        </Box>
        <Stack direction="row" spacing={0.5} alignItems="center">
          {editing ? (
            <>
              <Button
                size="small"
                variant="contained"
                startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveRounded sx={{ fontSize: 16 }} />}
                onClick={onSave}
                disabled={saving}
                sx={{ textTransform: "none", boxShadow: "none", "&:hover": { boxShadow: "none" }, height: 30 }}
              >
                Save
              </Button>
              <Button
                size="small"
                variant="text"
                onClick={onCancelEdit}
                disabled={saving}
                sx={{ textTransform: "none", height: 30 }}
              >
                Cancel
              </Button>
            </>
          ) : (
            <Tooltip title="Edit">
              <IconButton size="small" onClick={onStartEdit} aria-label="Edit definition">
                <EditRounded fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Close">
            <span>
              <IconButton
                size="small"
                onClick={onClose}
                disabled={saving}
                aria-label="Close"
              >
                <CloseRounded fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Stat strip — confidence bar + key timestamps */}
      {!editing && (
        <Box
          sx={(t) => ({
            display: "grid",
            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
            gap: 1.25,
            p: 1.25,
            borderRadius: `${t.ds.radius.md}px`,
            border: `1px solid ${t.tokens.border.subtle}`,
            bgcolor: t.tokens.surface.raised,
            mb: 2,
          })}
        >
          <DrawerStat
            label="Confidence"
            tooltip="Confidence reflects how reliably this definition has answered questions correctly. It rises with each validation and falls when corrections are needed."
          >
            <ConfidenceBar value={node.confidence} />
          </DrawerStat>
          <DrawerStat
            label="Last used"
            tooltip={node.last_used ? formatAbsolute(node.last_used) : "Not used yet"}
          >
            <Typography variant="body2" sx={{ fontSize: "0.8125rem", fontWeight: 600 }}>
              {node.last_used ? relativeTime(node.last_used) : "—"}
            </Typography>
          </DrawerStat>
          <DrawerStat
            label="Updated"
            tooltip={node.updated_at ? formatAbsolute(node.updated_at) : ""}
          >
            <Typography variant="body2" sx={{ fontSize: "0.8125rem", fontWeight: 600 }}>
              {node.updated_at ? relativeTime(node.updated_at) : "—"}
            </Typography>
          </DrawerStat>
        </Box>
      )}

      <Divider sx={{ mb: 2 }} />

      {/* Content - scrollable */}
      <Box sx={{ flex: 1, overflow: "auto" }}>
        {/* Definition */}
        <SectionTitle>Definition</SectionTitle>
        {editing ? (
          <TextField
            size="small"
            value={editDefinition}
            onChange={(e) => setEditDefinition(e.target.value)}
            multiline
            rows={3}
            fullWidth
            sx={{ mb: 2 }}
          />
        ) : (
          <Typography variant="body2" sx={{ mb: 2, whiteSpace: "pre-wrap", lineHeight: 1.55 }}>
            {node.content?.description || (
              <Box component="span" sx={{ color: "text.disabled", fontStyle: "italic" }}>
                No definition provided.
              </Box>
            )}
          </Typography>
        )}

        {/* SQL */}
        {(editing || sqlValue) && (
          <>
            <SectionTitle>{sqlLabel}</SectionTitle>
            {editing ? (
              <TextField
                size="small"
                value={editFormula}
                onChange={(e) => setEditFormula(e.target.value)}
                fullWidth
                multiline
                minRows={2}
                placeholder={
                  node.node_type === "business_rule"
                    ? "status = 'confirmed' AND created_at > NOW() - INTERVAL '90 days'"
                    : "SUM(amount) - SUM(refunds)"
                }
                sx={{ mb: 2, "& textarea": { fontFamily: "monospace", fontSize: "0.8125rem" } }}
              />
            ) : (
              <Paper
                variant="outlined"
                sx={(t) => ({
                  p: 1.5,
                  mb: 2,
                  borderRadius: `${t.ds.radius.sm}px`,
                  bgcolor: alpha(t.tokens.text.primary, t.palette.mode === "light" ? 0.04 : 0.08),
                })}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: "monospace",
                    fontSize: "0.8125rem",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {sqlValue}
                </Typography>
              </Paper>
            )}
          </>
        )}

        {/* Type (editable) */}
        {editing && (
          <FormControl size="small" sx={{ mb: 2, minWidth: 0, maxWidth: 240 }} fullWidth>
            <InputLabel>Type</InputLabel>
            <Select value={editType} label="Type" onChange={(e) => setEditType(e.target.value)}>
              <MenuItem value="metric">Metric</MenuItem>
              <MenuItem value="business_rule">Business Rule</MenuItem>
              <MenuItem value="term">Term</MenuItem>
            </Select>
          </FormControl>
        )}

        {/* Subtle hint about cmd/ctrl-S while editing */}
        {editing && !saving && (
          <Typography
            variant="caption"
            color="text.disabled"
            sx={{ display: "block", mb: 2, fontSize: "0.75rem" }}
          >
            Tip — press ⌘S / Ctrl+S to save.
          </Typography>
        )}

        {/* Changelog (read-only mode only) */}
        {!editing && (
          <>
            <SectionTitle icon={<HistoryRounded sx={{ fontSize: 16 }} />}>History</SectionTitle>
            {changelogLoading ? (
              <LinearProgress sx={{ mb: 2 }} />
            ) : changelog.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8125rem", mb: 2 }}>
                No history yet — this definition hasn't been edited.
              </Typography>
            ) : (
              <Stack spacing={1.5} sx={{ mb: 2 }}>
                {groupedChangelog.map((group) => (
                  <Box key={group.day}>
                    <Typography
                      variant="overline"
                      color="text.disabled"
                      sx={{ fontSize: "0.6875rem", letterSpacing: 0.8, display: "block", mb: 0.5 }}
                    >
                      {group.day}
                    </Typography>
                    <Stack spacing={0.75}>
                      {group.items.map((entry) => (
                        <HistoryEntry key={entry.changelog_id} entry={entry} />
                      ))}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            )}
          </>
        )}
      </Box>

      {/* Bottom action */}
      {!editing && (
        <Box sx={{ pt: 2, borderTop: 1, borderColor: "divider" }}>
          <Button
            variant="outlined"
            color={node.archived_at ? "primary" : "error"}
            size="small"
            startIcon={node.archived_at ? <UnarchiveRounded /> : <ArchiveRounded />}
            onClick={onArchive}
            fullWidth
            sx={{ textTransform: "none" }}
          >
            {node.archived_at ? "Restore Definition" : "Archive Definition"}
          </Button>
          {!node.archived_at && (
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{ display: "block", mt: 0.75, fontSize: "0.75rem", textAlign: "center" }}
            >
              Archived definitions are kept for reference but not used by the AI.
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}

// ============================================================================
// Drawer subcomponents
// ============================================================================

function TypeGlyph({ tone, icon }: { tone: Tone; icon: React.ReactElement }) {
  return (
    <Box
      aria-hidden
      sx={(t) => {
        const tonePalette: Record<Tone, string> = {
          info: t.tokens.semantic.info,
          warning: t.tokens.semantic.warning,
          success: t.tokens.semantic.success,
          neutral: t.tokens.text.secondary,
        };
        const c = tonePalette[tone];
        return {
          width: 40,
          height: 40,
          borderRadius: `${t.ds.radius.sm}px`,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: c,
          backgroundColor: alpha(c, 0.12),
          border: `1px solid ${alpha(c, 0.2)}`,
        };
      }}
    >
      {icon}
    </Box>
  );
}

function DrawerStat({
  label,
  tooltip,
  children,
}: {
  label: string;
  tooltip?: string;
  children: React.ReactNode;
}) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Tooltip title={tooltip || ""} placement="top" arrow>
        <Typography
          variant="overline"
          color="text.secondary"
          sx={{
            fontSize: "0.6875rem",
            letterSpacing: 0.8,
            display: "block",
            mb: 0.5,
            cursor: tooltip ? "help" : "default",
          }}
        >
          {label}
        </Typography>
      </Tooltip>
      {children}
    </Box>
  );
}

function HistoryEntry({ entry }: { entry: ChangelogEntry }) {
  const summaries = summarizeChange(entry);
  return (
    <Paper
      variant="outlined"
      sx={(t) => ({
        p: 1.25,
        borderRadius: `${t.ds.radius.sm}px`,
        bgcolor: t.tokens.surface.raised,
      })}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.25 }}>
        <Typography variant="caption" sx={{ fontWeight: 600 }}>
          {summaries[0]}
        </Typography>
        <Tooltip title={formatAbsolute(entry.created_at)}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
            {relativeTime(entry.created_at)}
          </Typography>
        </Tooltip>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
        by {formatActor(entry.actor_type)}
      </Typography>
      {summaries.slice(1).map((s, i) => (
        <Typography
          key={i}
          variant="caption"
          display="block"
          sx={{ mt: 0.25, fontSize: "0.75rem" }}
        >
          {s}
        </Typography>
      ))}
    </Paper>
  );
}

// ============================================================================
// Small reusable components
// ============================================================================

function SectionTitle({ children, icon }: { children: React.ReactNode; icon?: React.ReactElement }) {
  return (
    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.75 }}>
      {icon}
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ fontSize: "0.6875rem", letterSpacing: 1 }}
      >
        {children}
      </Typography>
    </Stack>
  );
}
